import { normalizeKey } from '../utils/normalizeDietKey';
import { formatOptionTitle, optionNumber } from '../utils/formatOptionTitle';

/** Build { [period]: { optionIds, foods } } from plan/template meals array. */
export function buildMealDataByPeriod(meals, periods) {
  const mealByType = new Map();
  (meals || []).forEach((m) => {
    const key = normalizeKey(m.meal_type_description || m.meal_type || '');
    if (key) mealByType.set(key, m);
  });

  const out = {};
  (periods || []).forEach((period) => {
    const meal = mealByType.get(normalizeKey(period));
    const optionIds = {};
    const foods = [];
    if (meal?.meal_options) {
      meal.meal_options.forEach((opt, idx) => {
        const optionKey = formatOptionTitle(opt.title ?? `Option ${idx + 1}`, idx);
        optionIds[optionKey] = opt.id;
        (opt.foods || []).forEach((f) => {
          if (!f?.food_id) return;
          foods.push({
            ...f,
            meal_type: meal.meal_type_description || meal.meal_type,
            meal_type_id: meal.meal_type_id,
            option: optionKey,
            deleteId: f.id,
            food_id: f.food_id,
          });
        });
      });
    }
    out[period] = { optionIds, foods };
  });
  return out;
}

/** Map normalized meal type key → meal id. */
export function buildMealIdMapFromMeals(meals) {
  const map = {};
  (meals || []).forEach((m) => {
    const k = normalizeKey(m.meal_type_description || m.meal_type || '');
    if (k) map[k] = m.id;
  });
  return map;
}

/** Sorted option numbers (1, 2, …) present in meals. */
export function extractAvailableOptionNumbers(meals) {
  const opts = new Set();
  (meals || []).forEach((meal) => {
    meal.meal_options?.forEach((opt, idx) => {
      opts.add(optionNumber(opt.title, idx));
    });
  });
  return Array.from(opts).sort((a, b) => Number(a) - Number(b));
}

/** Foods belonging to a given option number. */
export function filterFoodsByOptionNumber(foods, optionNum) {
  const want = optionNumber(optionNum);
  return (foods || []).filter((f) => optionNumber(f.option) === want);
}

/** Option numbers present in meals that have zero foods across every meal type. */
export function extractEmptyOptionNumbers(meals) {
  return extractAvailableOptionNumbers(meals).filter((optNum) => {
    let hasFood = false;
    (meals || []).forEach((meal) => {
      (meal.meal_options || []).forEach((opt) => {
        if (optionNumber(opt.title) !== optNum) return;
        if ((opt.foods?.length ?? 0) > 0) hasFood = true;
      });
    });
    return !hasFood;
  });
}

/** Option numbers for a meal type from its optionIds map. */
export function extractOptionNumbersFromOptionIds(optionIds = {}) {
  return Object.keys(optionIds)
    .map((t) => optionNumber(t))
    .filter(Boolean)
    .sort((a, b) => Number(a) - Number(b));
}

/** All meal_option rows (across meal types) whose title matches optionNum. */
export function findMealOptionsByNumber(meals, optionNum) {
  if (optionNum == null || optionNum === '') return [];
  const want = optionNumber(optionNum);
  const matches = [];
  (meals || []).forEach((meal) => {
    (meal.meal_options || []).forEach((opt) => {
      if (optionNumber(opt.title) !== want) return;
      matches.push({
        mealId: meal.id,
        mealTypeDescription: meal.meal_type_description || meal.meal_type,
        optionId: opt.id,
        foodsCount: opt.foods?.length ?? 0,
      });
    });
  });
  return matches;
}
